'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import Link from 'next/link';
import type { SearchEntry, SearchEntryType } from '@/lib/seo/search-index';

interface SiteSearchProps {
  entries: SearchEntry[];
}

const TYPE_LABELS: Record<SearchEntryType, string> = {
  blog: 'Article',
  guide: 'Guide',
  template: 'Template',
  report: 'Report',
  page: 'Page',
};

const TYPE_BADGE_CLASSES: Record<SearchEntryType, string> = {
  blog: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  guide: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  template: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  report: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  page: 'bg-slate-500/10 text-slate-300 border-slate-500/20',
};

const FILTERS: Array<{ value: 'all' | SearchEntryType; label: string }> = [
  { value: 'all', label: 'Everything' },
  { value: 'guide', label: 'Guides' },
  { value: 'blog', label: 'Articles' },
  { value: 'template', label: 'Templates' },
  { value: 'report', label: 'Reports' },
  { value: 'page', label: 'Pages' },
];

const MAX_RESULTS = 40;

/**
 * Score one entry against the search terms.
 *
 * Weighted so a title hit outranks a keyword hit, and an exact phrase match
 * outranks the same words scattered across fields. Returns 0 when any term is
 * missing entirely, making the match an AND across terms rather than an OR —
 * without that, a two-word query returns almost the whole site.
 */
function scoreEntry(entry: SearchEntry, terms: string[], phrase: string): number {
  const title = entry.title.toLowerCase();
  const excerpt = entry.excerpt.toLowerCase();
  const keywords = entry.terms.toLowerCase();

  let score = 0;

  for (const term of terms) {
    const inTitle = title.includes(term);
    const inExcerpt = excerpt.includes(term);
    const inKeywords = keywords.includes(term);

    if (!inTitle && !inExcerpt && !inKeywords) return 0;

    if (inTitle) score += 10;
    if (inKeywords) score += 4;
    if (inExcerpt) score += 2;
  }

  // Whole-phrase hits are almost always what the searcher meant.
  if (terms.length > 1) {
    if (title.includes(phrase)) score += 25;
    else if (keywords.includes(phrase) || excerpt.includes(phrase)) score += 10;
  }

  // A title that opens with the query is a stronger signal than one that
  // merely contains it somewhere in the middle.
  if (title.startsWith(phrase)) score += 15;

  return score;
}

/**
 * Client-side search across every content collection on the site.
 *
 * Applies an initial query from `?q=` so the page can be linked to directly —
 * this is the target of the WebSite SearchAction in the site's structured data,
 * so Google's sitelinks searchbox lands here with the query already applied.
 *
 * That parameter is read from `window.location` in an effect rather than via
 * `useSearchParams`, deliberately. `useSearchParams` opts the whole subtree out
 * of prerendering, which left the served HTML containing only the Suspense
 * fallback: no result links for a crawler to follow, and an empty page without
 * JavaScript. Reading it after mount keeps the default list in the static HTML
 * as real anchors while `?q=` still works on load.
 *
 * @param entries - The full search index, built server-side
 *
 * @example
 * ```tsx
 * <SiteSearch entries={buildSearchIndex()} />
 * ```
 */
export function SiteSearch({ entries }: SiteSearchProps) {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [type, setType] = useState<'all' | SearchEntryType>('all');

  // Apply ?q= once on mount, and again on back/forward navigation.
  useEffect(() => {
    const applyFromUrl = () => {
      const q = new URLSearchParams(window.location.search).get('q') ?? '';
      setInput(q);
      setQuery(q);
    };

    applyFromUrl();
    window.addEventListener('popstate', applyFromUrl);
    return () => window.removeEventListener('popstate', applyFromUrl);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(input), 200);
    return () => clearTimeout(timer);
  }, [input]);

  const results = useMemo(() => {
    const pool = type === 'all' ? entries : entries.filter((entry) => entry.type === type);

    const phrase = query.trim().toLowerCase();
    if (phrase.length < 2) {
      // No query: list the whole collection rather than showing an empty
      // state. This is the page's static form, so it doubles as an HTML
      // sitemap — every page on the site reachable in one hop, as real
      // anchors in the prerendered markup.
      return pool;
    }

    const terms = phrase.split(/\s+/).filter(Boolean);

    return pool
      .map((entry) => ({ entry, score: scoreEntry(entry, terms, phrase) }))
      .filter((result) => result.score > 0)
      .sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title))
      .slice(0, MAX_RESULTS)
      .map((result) => result.entry);
  }, [entries, query, type]);

  const hasQuery = query.trim().length >= 2;

  return (
    <div>
      <div className="relative mb-6">
        <Search
          className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground"
          aria-hidden="true"
        />
        <label htmlFor="site-search-input" className="sr-only">
          Search Email Ferret
        </label>
        <input
          id="site-search-input"
          type="search"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Search guides, articles, templates and comparisons..."
          className="w-full rounded-xl border border-border bg-card/60 py-4 pl-12 pr-12 text-foreground placeholder:text-muted-foreground focus:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        />
        {input.length > 0 && (
          <button
            type="button"
            onClick={() => setInput('')}
            aria-label="Clear search"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setType(filter.value)}
            aria-pressed={type === filter.value}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
              type === filter.value
                ? 'border-blue-500/40 bg-blue-500/15 text-blue-300'
                : 'border-border bg-card/40 text-muted-foreground hover:text-foreground'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <p className="mb-6 text-sm text-muted-foreground" aria-live="polite">
        {hasQuery
          ? `${results.length} result${results.length === 1 ? '' : 's'} for "${query.trim()}"`
          : `Browsing ${results.length} of ${entries.length} pages`}
      </p>

      {results.length === 0 ? (
        <div className="rounded-xl border border-border bg-card/40 p-8 text-center">
          <p className="mb-2 text-foreground">No results for &quot;{query.trim()}&quot;</p>
          <p className="text-sm text-muted-foreground">
            Try a broader term, or browse the{' '}
            <Link href="/guides" className="text-blue-400 hover:underline">
              guides
            </Link>
            ,{' '}
            <Link href="/blog" className="text-blue-400 hover:underline">
              blog
            </Link>{' '}
            or{' '}
            <Link href="/glossary" className="text-blue-400 hover:underline">
              glossary
            </Link>
            .
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {results.map((entry) => (
            <li key={entry.href}>
              <Link
                href={entry.href}
                className="block rounded-xl border border-border bg-card/40 p-5 transition-colors hover:border-blue-500/40 hover:bg-card/70"
              >
                <div className="mb-2 flex items-start justify-between gap-4">
                  <h2 className="text-lg font-semibold text-foreground">{entry.title}</h2>
                  <span
                    className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs ${TYPE_BADGE_CLASSES[entry.type]}`}
                  >
                    {TYPE_LABELS[entry.type]}
                  </span>
                </div>
                {entry.excerpt && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{entry.excerpt}</p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
